import { MouseEvent, useMemo, useState } from 'react';
import { css, Theme } from '@emotion/react';
import { GitBranch, CaretDown, GitCommit } from '@phosphor-icons/react';
import { useRepository } from 'client/hooks/useRepository';
import { Button } from 'client/components/Button';
import { CatalogItem } from 'client/components/Catalog';
import { SearchableDropdown, SearchableDropdownRenderOptionFunction } from 'client/components/Dropdown/SearchableDropdown';
import { ListItemText } from 'client/components/Other/ListItemText';
import { borderRadius } from 'client/Theme';

export interface RepositoryCatalogRepositoryBranch {
  id: string;
  name: string;
  commit: string;
}

const renderBranchOption: SearchableDropdownRenderOptionFunction<RepositoryCatalogRepositoryBranch> = (props, option, { selected }) => {
  return (
    <li {...props} css={optionStyle}>
      <GitBranch weight="bold" />
      <ListItemText
        primary={option.name}
        secondary={
          <>
            <GitCommit weight="bold" /> {option.commit}
          </>
        }
      />
    </li>
  );
};

interface RepositoryCatalogItemProps {
  projectID: number | null | undefined;
  repositoryRef: string | null;
  onChangeBranch?: (branch: RepositoryCatalogRepositoryBranch | null) => void;
}

export const RepositoryCatalogItem = ({ projectID, repositoryRef, onChangeBranch }: RepositoryCatalogItemProps) => {
  const [branchEl, setBranchEl] = useState<HTMLElement | null>(null);

  const { repository: repositoryData, repositoryItems } = useRepository({
    projectID,
    includeBranches: true,
  });

  const branchOptions = useMemo(
    () =>
      (repositoryData?.branches || []).map((item) => ({
        id: item.name,
        name: item.name,
        commit: item.commit.sha.slice(0, 7),
      })),
    [repositoryData]
  );

  const handleBranchClick = (event: MouseEvent<HTMLButtonElement>) => setBranchEl(event.currentTarget);
  const handleBranchClose = () => setBranchEl(null);
  const handleBranchChange = (branch: RepositoryCatalogRepositoryBranch | null) => onChangeBranch && onChangeBranch(branch);

  const item = useMemo(() => {
    const item = { ...repositoryItems[0] };
    if (onChangeBranch) {
      item.action = (
        <>
          <Button icon={<CaretDown weight="fill" />} small css={branchButtonStyle} disabled={!repositoryData} onClick={handleBranchClick}>
            <GitBranch weight="bold" /> {repositoryRef || 'Switch branch'}
          </Button>
          {item.action}
        </>
      );
    }
    return item;
  }, [repositoryItems[0], repositoryRef, repositoryData, onChangeBranch]);

  return (
    <>
      <CatalogItem {...item} />
      <SearchableDropdown
        anchorEl={branchEl}
        value={repositoryRef}
        options={branchOptions}
        renderOption={renderBranchOption}
        popoverProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        }}
        onClose={handleBranchClose}
        onChange={handleBranchChange}
      />
    </>
  );
};

const branchButtonStyle = (theme: Theme) => css`
  margin-right: 8px;

  > span:first-of-type > svg {
    transform: translate(-3px, 1px);
    color: ${theme.color.grey[100]};
  }
`;

const optionStyle = css`
  border-radius: ${borderRadius}px;
  overflow: hidden;

  > svg {
    margin-right: 8px;
  }

  > .MuiListItemText-root {
    > .MuiListItemText-primary {
      font-weight: bold;
    }

    > .MuiListItemText-secondary {
      display: flex;
      align-items: center;

      > .tag {
        margin-right: 6px;
        cursor: pointer;
      }

      > svg {
        margin-right: 2px;
      }
    }
  }
`;
